import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { transportAPI } from '../../services/api';
import AppNavbar from '../../components/AppNavbar';
import SiteFooter from '../../components/SiteFooter';
import MobileBottomNav from '../../components/MobileBottomNav';
import StarRating from '../../components/StarRating';

const STATUS_CFG = {
  payment_pending:   { label: 'بانتظار الدفع', color: 'bg-amber-100 text-amber-700' },
  payment_submitted: { label: 'إيصال قيد المراجعة', color: 'bg-amber-100 text-amber-700' },
  payment_confirmed: { label: 'بانتظار تعيين مركبة', color: 'bg-blue-100 text-blue-700' },
  vehicle_assigned:  { label: 'بانتظار الاستلام', color: 'bg-indigo-100 text-indigo-700' },
  picked_up:         { label: 'تم الاستلام', color: 'bg-purple-100 text-purple-700' },
  in_transit:        { label: 'في الطريق', color: 'bg-purple-100 text-purple-700' },
  delivered:         { label: 'تم التسليم', color: 'bg-emerald-100 text-emerald-700' },
  completed:         { label: 'مكتمل', color: 'bg-emerald-100 text-emerald-700' },
  cancelled:         { label: 'ملغى', color: 'bg-red-100 text-red-700' },
};

const EVENT_LABELS = {
  created: 'تم إنشاء الطلب', payment_submitted: 'تم رفع إيصال الدفع', payment_confirmed: 'تم تأكيد الدفع',
  vehicle_assigned: 'تم تعيين مركبة وسائق', picked_up: 'تم استلام البضاعة', in_transit: 'البضاعة في الطريق',
  delivered: 'تم تسليم البضاعة',
};

const TransportOrderDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [order, setOrder]     = useState(null);
  const [loading, setLoading] = useState(true);
  const [receipt, setReceipt] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [ratingValue, setRatingValue] = useState(0);
  const [ratingComment, setRatingComment] = useState('');
  const [submittingReview, setSubmittingReview] = useState(false);
  const [bankAccounts, setBankAccounts] = useState([]);

  // Transport orders aren't tied to any bus company/trip — this is the only
  // place bank details for them come from (see backend's PlatformBankAccount).
  useEffect(() => {
    transportAPI.getBankAccounts().then((res) => setBankAccounts(res.data.data || [])).catch(() => {});
  }, []);

  // setLoading(true) is only ever hit via the initial useState(true) — never
  // called again inside load() — so background polls update the order
  // silently instead of re-showing the full-page spinner on every refresh
  // (same pattern as RideOrderDetailPage.jsx's own load()).
  const load = useCallback(() => {
    transportAPI.getOrderById(id)
      .then((res) => setOrder(res.data.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    let interval = setInterval(load, 15000); // cargo — less time-sensitive than rides, same interval as TransportRequestDetailPage
    const handleVisibility = () => {
      if (document.hidden) {
        clearInterval(interval);
      } else {
        load();
        interval = setInterval(load, 15000);
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [load]);

  const handleUpload = async () => {
    if (!receipt) { alert('يرجى اختيار صورة الإيصال'); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('receipt', receipt);
      await transportAPI.uploadPaymentProof(id, fd);
      load();
    } catch (err) {
      alert(err.response?.data?.message || 'تعذّر رفع الإيصال');
    } finally { setUploading(false); }
  };

  const handleSubmitReview = async () => {
    if (!ratingValue) { alert('يرجى اختيار تقييم'); return; }
    setSubmittingReview(true);
    try {
      await transportAPI.submitReview(id, { rating: ratingValue, comment: ratingComment || undefined });
      load();
    } catch (err) {
      alert(err.response?.data?.message || 'تعذّر إرسال التقييم');
    } finally { setSubmittingReview(false); }
  };

  if (loading) return <div dir="rtl" className="min-h-screen bg-[#F5F9FF]"><AppNavbar /><p className="text-center py-16 text-gray-400">جاري التحميل...</p></div>;
  if (!order) return <div dir="rtl" className="min-h-screen bg-[#F5F9FF]"><AppNavbar /><p className="text-center py-16 text-gray-400">الطلب غير موجود</p></div>;

  const req = order.request || {};
  const st = STATUS_CFG[order.status] || { label: order.status, color: 'bg-gray-100 text-gray-600' };

  return (
    <div dir="rtl" className="min-h-screen bg-[#F5F9FF] pb-16">
      <AppNavbar />
      <div className="max-w-2xl mx-auto px-4 py-6">
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 mb-4">
          <div className="flex items-center justify-between mb-3">
            <h1 className="font-black text-gray-800">{order.orderNumber}</h1>
            <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${st.color}`}>{st.label}</span>
          </div>
          <p className="text-sm font-semibold text-gray-700 mb-1">{req.fromLabel} ← {req.toLabel}</p>
          <p className="text-lg font-black text-blue-700">{order.price} ج.س</p>
        </div>

        {order.status === 'payment_pending' && (
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 mb-4">
            <h2 className="font-bold text-gray-800 mb-1">إتمام الدفع</h2>
            {bankAccounts.length === 0 ? (
              <p className="text-xs text-gray-500 mb-4">حوّل المبلغ عبر التواصل مع خدمة العملاء، ثم ارفع صورة إيصال التحويل هنا</p>
            ) : (
              <>
                <p className="text-xs text-gray-500 mb-3">حوّل المبلغ إلى أحد الحسابات التالية، ثم ارفع صورة إيصال التحويل هنا</p>
                <div className="space-y-2 mb-4">
                  {bankAccounts.map((b) => (
                    <div key={b.id} className="flex items-center gap-3 p-3 rounded-xl bg-[#F5F9FF] border border-gray-100">
                      <div className="w-9 h-9 rounded-lg bg-[#0D9488] flex items-center justify-center text-white text-sm">🏦</div>
                      <div>
                        <p className="font-bold text-sm text-gray-800">{b.bankName}</p>
                        <p className="text-xs text-gray-500">{b.accountNumber} — {b.accountHolder}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
            <label className="block text-xs font-bold text-gray-600 mb-1.5">إيصال التحويل</label>
            <input type="file" accept="image/*,application/pdf" onChange={(e) => setReceipt(e.target.files[0])} className="text-sm mb-4" />
            <button onClick={handleUpload} disabled={uploading || !receipt}
              className="w-full bg-[#1E40AF] hover:bg-[#1D4ED8] disabled:opacity-50 text-white font-black py-3 rounded-xl transition-all">
              {uploading ? '...جارٍ الرفع' : 'رفع الإيصال'}
            </button>
          </div>
        )}

        {order.status === 'payment_submitted' && (
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5 text-sm text-amber-800 mb-4">
            تم استلام إيصال الدفع — بانتظار مراجعة فريق سافر.
          </div>
        )}

        {['vehicle_assigned', 'picked_up', 'in_transit', 'delivered', 'completed'].includes(order.status) && order.driverName && (
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 mb-4">
            <h2 className="font-bold text-gray-800 mb-2">🚚 بيانات السائق والمركبة</h2>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div><p className="text-xs text-gray-500">السائق</p><p className="font-semibold text-gray-800">{order.driverName}</p></div>
              <div><p className="text-xs text-gray-500">الهاتف</p><p className="font-semibold text-gray-800" dir="ltr">{order.driverPhone}</p></div>
              {order.vehiclePlate && <div><p className="text-xs text-gray-500">رقم اللوحة</p><p className="font-semibold text-gray-800">{order.vehiclePlate}</p></div>}
            </div>
          </div>
        )}

        {order.status === 'vehicle_assigned' && order.pickupOtp && (
          <div className="bg-blue-50 border border-blue-100 rounded-2xl p-5 mb-4 text-center">
            <p className="text-xs text-blue-700 font-bold mb-1">رمز الاستلام — قدّمه للسائق عند تحميل البضاعة</p>
            <p className="text-3xl font-black tracking-widest text-blue-900 font-mono">{order.pickupOtp}</p>
          </div>
        )}

        {['picked_up', 'in_transit'].includes(order.status) && order.deliveryOtp && (
          <div className="bg-blue-50 border border-blue-100 rounded-2xl p-5 mb-4 text-center">
            <p className="text-xs text-blue-700 font-bold mb-1">رمز التسليم — قدّمه للسائق عند استلام البضاعة في الوجهة</p>
            <p className="text-3xl font-black tracking-widest text-blue-900 font-mono">{order.deliveryOtp}</p>
          </div>
        )}

        {['delivered', 'completed'].includes(order.status) && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-5 text-sm text-emerald-800 text-center font-bold mb-4">
            ✅ تم تسليم بضاعتكم بنجاح
          </div>
        )}

        {order.status === 'delivered' && (
          order.review ? (
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 mb-4 text-center">
              <p className="text-xs text-gray-500 mb-2">تقييمك للمزود</p>
              <div className="flex justify-center"><StarRating value={order.review.rating} readOnly /></div>
              {order.review.comment && <p className="text-sm text-gray-600 mt-2">{order.review.comment}</p>}
            </div>
          ) : (
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 mb-4">
              <h2 className="font-bold text-gray-800 mb-3 text-center">قيّم تجربتك مع المزود</h2>
              <div className="flex justify-center mb-3"><StarRating value={ratingValue} onChange={setRatingValue} /></div>
              <textarea rows={2} placeholder="تعليق (اختياري)" value={ratingComment} onChange={(e) => setRatingComment(e.target.value)}
                className="w-full border-2 border-gray-200 rounded-xl px-4 py-2.5 text-sm mb-3 outline-none focus:border-[#1E3A8A]" />
              <button onClick={handleSubmitReview} disabled={submittingReview || !ratingValue}
                className="w-full bg-[#1E40AF] hover:bg-[#1D4ED8] disabled:opacity-50 text-white font-bold py-2.5 rounded-xl transition-all">
                {submittingReview ? '...جارٍ الإرسال' : 'إرسال التقييم'}
              </button>
            </div>
          )
        )}

        {order.events?.length > 0 && (
          <div className="mt-2">
            <p className="text-xs font-bold text-gray-600 mb-2">سجل الطلب</p>
            <div className="space-y-2">
              {order.events.map((ev) => (
                <div key={ev.id} className="flex items-center gap-3 text-xs">
                  <span className="w-1.5 h-1.5 rounded-full bg-blue-500 flex-shrink-0" />
                  <span className="text-gray-700 font-semibold">{EVENT_LABELS[ev.event] || ev.event}</span>
                  <span className="text-gray-400">{new Date(ev.createdAt).toLocaleString('ar-SA')}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      <div className="hidden md:block"><SiteFooter navigate={navigate} /></div>
      <MobileBottomNav />
    </div>
  );
};

export default TransportOrderDetailPage;
